import { MobileSelect } from '@/components/ui';
import type { PhotoFile } from '../types';


interface PhotoPreviewCardProps {
  photo: PhotoFile;
  onRemove: (id: string) => void;
  onProjectChange?: (id: string, projectId: string | null) => void;
  onAnnotate?: (id: string) => void;
  projects?: Array<{ id: string; name: string }>;
  showProjectSelect?: boolean;
}

export function PhotoPreviewCard({
  photo,
  onRemove,
  onProjectChange,
  onAnnotate,
  projects = [],
  showProjectSelect = true,
}: PhotoPreviewCardProps) {
  const statusColors = {
    pending: 'bg-secondary-100 text-secondary-600',
    extracting: 'bg-blue-100 text-blue-600',
    matching: 'bg-blue-100 text-blue-600',
    ready: 'bg-green-100 text-green-600',
    uploading: 'bg-yellow-100 text-yellow-600',
    uploaded: 'bg-green-100 text-green-600',
    error: 'bg-red-100 text-red-600',
  };

  const statusLabels = {
    pending: 'Pending',
    extracting: 'Reading...',
    matching: 'Matching...',
    ready: 'Ready',
    uploading: 'Uploading...',
    uploaded: 'Uploaded',
    error: 'Error',
  };

  const confidenceColors = {
    high: 'text-green-600',
    medium: 'text-yellow-600',
    low: 'text-orange-600',
    none: 'text-secondary-400',
  };

  return (
    <div className="bg-white rounded-lg border border-secondary-200 overflow-hidden">
      {/* Image Preview */}
      <div className="relative aspect-square bg-secondary-100">
        <img
          src={photo.preview}
          alt={photo.file.name}
          className="w-full h-full object-cover"
        />
        
        {/* Remove Button */}
        <button
          onClick={() => onRemove(photo.id)}
          className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
          disabled={photo.status === 'uploading'}
        >
          <svg className="w-4 h-4 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Status Badge */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${statusColors[photo.status]}`}>
          {statusLabels[photo.status]}
        </div>

        {/* Annotation Indicator */}
        {photo.description && (
          <div className="absolute bottom-2 left-2 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center shadow">
            <svg className="w-3.5 h-3.5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h6v6h6v10H6z" />
              <path d="M8 12h8v2H8zm0 4h8v2H8z" />
            </svg>
          </div>
        )}

        {/* Upload Progress */}
        {photo.status === 'uploading' && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary-200">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${photo.uploadProgress || 0}%` }}
            />
          </div>
        )}
      </div>

      {/* Photo Info */}
      <div className="p-3 space-y-2">
        {/* File Name */}
        <p className="text-sm font-medium text-secondary-900 truncate" title={photo.file.name}>
          {photo.file.name}
        </p>

        {/* EXIF Info */}
        {photo.exif.takenAt && (
          <p className="text-xs text-secondary-500">
            {new Date(photo.exif.takenAt).toLocaleDateString()} at{' '}
            {new Date(photo.exif.takenAt).toLocaleTimeString()}
          </p>
        )}

        {/* GPS Status */}
        {photo.exif.lat && photo.exif.lng ? (
          <div className="flex items-center gap-1 text-xs text-green-600">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
            <span>Location found</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs text-secondary-400">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
            <span>No location data</span>
          </div>
        )}

        {/* Annotate Button */}
        {onAnnotate && (photo.status === 'ready' || photo.status === 'pending') && (
          <button
            onClick={() => onAnnotate(photo.id)}
            className={`
              w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors
              ${photo.description
                ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200'
                : 'bg-white text-secondary-700 hover:bg-secondary-50 border border-secondary-300'
              }
            `}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            <span>{photo.description ? 'Edit Notes' : 'Add Notes'}</span>
          </button>
        )}

        {/* Suggested Project Match */}
        {photo.suggestedProject && (
          <div className="bg-secondary-50 rounded p-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-secondary-700">Suggested:</span>
              <span className={`text-xs font-medium ${confidenceColors[photo.suggestedProject.confidence]}`}>
                {photo.suggestedProject.confidence} match
              </span>
            </div>
            <p className="text-sm text-secondary-900 truncate">
              {photo.suggestedProject.projectName}
            </p>
            <p className="text-xs text-secondary-500">
              {Math.round(photo.suggestedProject.distanceM)}m away
            </p>
          </div>
        )}

        {/* Project Select */}
{showProjectSelect && photo.status === 'ready' && onProjectChange && (
  <MobileSelect
    label="Assign to Project"
    value={photo.selectedProjectId || ''}
    onChange={(value) => onProjectChange(photo.id, value || null)}
    placeholder="No project (unassigned)"
    options={[
      { value: '', label: 'No project (unassigned)' },
      ...(photo.suggestedProject
        ? [{
            value: photo.suggestedProject.projectId,
            label: `✓ ${photo.suggestedProject.projectName}`,
            sublabel: `${Math.round(photo.suggestedProject.distanceM)}m away`,
          }]
        : []),
      ...projects
        .filter((p) => p.id !== photo.suggestedProject?.projectId)
        .map((project) => ({
          value: project.id,
          label: project.name,
        })),
    ]}
  />
)}

        {/* Error Message */}
        {photo.status === 'error' && photo.error && (
          <p className="text-xs text-red-600">{photo.error}</p>
        )}
      </div>
    </div>
  );
}